
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useProject } from "@/hooks/useProject";
import { Rocket, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CreateLaunchDialog } from "./components/CreateLaunchDialog";
import { DeleteLaunchDialog } from "./components/DeleteLaunchDialog";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";

const LaunchList = () => {
  const navigate = useNavigate();
  const { projectClient, currentProject } = useProject();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedLaunch, setSelectedLaunch] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const { data: launches, isLoading } = useQuery({
    queryKey: ["launches", currentProject],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from("form_launches")
        .select(`
          *,
          clientes!inner (id, nombre),
          report_templates (id, name),
          jotform_form_mappings:form_mapping_id (id, jotform_form_id, form_title),
          report_templates_v2:template_v2_id (id, name)
        `)
        .eq('project_id', projectClient.getProjectId())
        .order("created_at", { ascending: false });

      if (error) {
        toast.error("Error al cargar los lanzamientos");
        throw error;
      }

      return data;
    },
  });

  const { data: formCodes } = useQuery({
    queryKey: ["form_codes", currentProject],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from("form_codes")
        .select("form_launch_id, code")
        .eq('project_id', projectClient.getProjectId())
        .order("created_at", { ascending: false });
      
      if (error) {
        toast.error("Error al cargar los códigos de formulario");
        throw error;
      }
      
      return data.reduce((acc: Record<number, string[]>, code) => {
        if (!acc[code.form_launch_id]) {
          acc[code.form_launch_id] = [];
        }
        acc[code.form_launch_id].push(code.code);
        return acc;
      }, {});
    },
  });


  const handleDelete = (launch: any) => {
    setSelectedLaunch(launch);
    setIsDeleteDialogOpen(true);
  };

  const handleViewDetails = (launch: any) => {
    navigate(`/admin/launches/${launch.id}`);
  };
  
  const formatReportLifetime = (ms: number | null) => {
    if (!ms) return 'Sin expiración';
    return `${Math.round(ms / (24 * 60 * 60 * 1000))} días`;
  };

  const isV2Launch = (launch: any) => launch.template_v2_id !== null;

  const getFormDisplay = (launch: any) => {
    // Ahora todos usan jotform_form_mappings (tabla forms fue eliminada)
    if (launch.jotform_form_mappings) {
      return launch.jotform_form_mappings.form_title || launch.jotform_form_mappings.jotform_form_id;
    }
    return 'N/A';
  };

  const getTemplatesDisplay = (launch: any) => {
    if (isV2Launch(launch) && launch.report_templates_v2) {
      return launch.report_templates_v2.name;
    }
    return launch.report_templates?.name || 'N/A';
  };

  return (
    <div className="space-y-6">
      <DataTable
        title="Lanzamientos de Formularios"
        data={launches}
        isLoading={isLoading}
        onCreateNew={() => setIsCreateDialogOpen(true)}
        createButtonLabel="Nuevo Lanzamiento"
        emptyIcon={<Rocket className="w-10 h-10 text-muted-foreground" />}
        emptyTitle="No hay lanzamientos todavía"
        emptyDescription="No hay lanzamientos de formularios. Crea uno nuevo para comenzar."
        columns={[
          {
            header: "ID",
            key: "id" as any,
          },
          {
            header: "Versión",
            render: (launch) => (
              <Badge variant={isV2Launch(launch) ? "default" : "secondary"}>
                {isV2Launch(launch) ? "V2" : "Legacy"}
              </Badge>
            ),
          },
          {
            header: "Formulario",
            render: (launch) => getFormDisplay(launch),
          },
          {
            header: "Cliente",
            render: (launch) => (launch.clientes as any)?.nombre || "N/A",
          },
          {
            header: "Plantilla",
            render: (launch) => (
              <div className="max-w-[200px] truncate" title={getTemplatesDisplay(launch)}>
                {getTemplatesDisplay(launch)}
              </div>
            ),
          },
          {
            header: "Máx. Respuestas",
            render: (launch) => launch.max_respuestas || "Sin límite",
          },
          {
            header: "Vida del Reporte",
            render: (launch) => formatReportLifetime(launch.report_lifetime),
          },
          {
            header: "Códigos",
            render: (launch) => (
              <Badge variant="secondary">
                {formCodes && formCodes[launch.id]
                  ? formCodes[launch.id].length
                  : 0}
              </Badge>
            ),
          },
          {
            header: "Fecha",
            render: (launch) =>
              new Date(launch.created_at).toLocaleDateString(),
          },
          {
            header: "Acciones",
            className: "text-right",
            render: (launch) => (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(launch);
                }}
                title="Eliminar"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ),
          },
        ]}
        onRowClick={handleViewDetails}
      />

      <CreateLaunchDialog 
        open={isCreateDialogOpen} 
        onOpenChange={setIsCreateDialogOpen}
      />
      
      {selectedLaunch && (
        <DeleteLaunchDialog
          launch={selectedLaunch}
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        />
      )}
    </div>
  );
};

export default LaunchList;
